/**
 * Coordinate math shared by the widget (which captures) and the designer app
 * (which replays).
 *
 * Every annotation is stored in *document* pixels together with the session's
 * `page_width`. Consumers derive their own scale from that width, so a replica
 * or a crop renders identically regardless of how wide the client's page was.
 */

import type { NoteType } from './types'

export type Point = [number, number]

export interface BoundingBox {
  minX: number
  minY: number
  maxX: number
  maxY: number
}

/** Geometry of a note, before its centroid has been resolved. */
export interface NoteGeometry {
  type: NoteType
  x?: number
  y?: number
  w?: number
  h?: number
  points?: Point[]
}

export interface Centroid {
  cx: number
  cy: number
}

export interface CropOptions {
  cropW: number
  cropH: number
  scale: number
  pageWidth: number
  pageHeight: number
}

export interface CropTransform {
  /** Translation applied to the scaled page so the annotation sits in view. */
  tx: number
  ty: number
  /** Where to draw the ring marker, in crop-local pixels. */
  ringX: number
  ringY: number
}

export function boundingBox(points: Point[]): BoundingBox {
  if (points.length === 0) return { minX: 0, minY: 0, maxX: 0, maxY: 0 }
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const [x, y] of points) {
    if (x < minX) minX = x
    if (x > maxX) maxX = x
    if (y < minY) minY = y
    if (y > maxY) maxY = y
  }
  return { minX, minY, maxX, maxY }
}

/**
 * The single point a note is considered to be "at". Crops and replica dots use
 * only this, which is why all three note types collapse to one representation.
 */
export function centroid(note: NoteGeometry): Centroid {
  switch (note.type) {
    case 'pin':
      return { cx: note.x ?? 0, cy: note.y ?? 0 }
    case 'rect':
      return {
        cx: (note.x ?? 0) + (note.w ?? 0) / 2,
        cy: (note.y ?? 0) + (note.h ?? 0) / 2,
      }
    case 'draw': {
      const box = boundingBox(note.points ?? [])
      return { cx: (box.minX + box.maxX) / 2, cy: (box.minY + box.maxY) / 2 }
    }
  }
}

/**
 * Scale that fits a page of `pageWidth` document pixels into `targetWidth`
 * rendered pixels. Falls back to 1 for a nonsense page width rather than
 * producing Infinity downstream.
 */
export function scaleFor(targetWidth: number, pageWidth: number): number {
  if (!Number.isFinite(pageWidth) || pageWidth <= 0) return 1
  return targetWidth / pageWidth
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max))
}

/**
 * Positions a scaled page inside a fixed-size crop window so the annotation is
 * centered, without ever revealing empty gutter beyond the page edges.
 *
 * The ideal offset centers the annotation: `cropW/2 - cx*scale`. It is then
 * clamped into `[cropW - pageWidth*scale, 0]`. When the scaled page is narrower
 * than the crop that interval is inverted, so the lower bound is itself clamped
 * to at most 0 and the page simply sits flush at the origin.
 */
export function cropTransform(a: Centroid, o: CropOptions): CropTransform {
  const scaledW = Math.max(0, o.pageWidth) * o.scale
  const scaledH = Math.max(0, o.pageHeight) * o.scale

  const minTx = Math.min(0, o.cropW - scaledW)
  const minTy = Math.min(0, o.cropH - scaledH)

  const tx = clamp(o.cropW / 2 - a.cx * o.scale, minTx, 0)
  const ty = clamp(o.cropH / 2 - a.cy * o.scale, minTy, 0)

  return { tx, ty, ringX: a.cx * o.scale + tx, ringY: a.cy * o.scale + ty }
}
